'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';
import useIsOnscreen from '@/hooks/use-is-onscreen';

import { ContentContext } from '@/components/ContentProvider';
import HitCounter from '@/components/HitCounter';
import Spacer from '@/components/Spacer';

function BlogPostContentFooter() {
  const { contentData, metrics } = React.useContext(ContentContext);
  const humanReadableDate =
    contentData.formattedDates.updatedOn?.humanReadable ??
    contentData.formattedDates.publishedOn.humanReadable;

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // The Hit Counter I created years ago is incredibly inefficient, in terms of DOM structure; it creates a BUNCH of nodes. We can wait until the user scrolls to this point before adding that stuff.
  const hasReachedFooter = useIsOnscreen(wrapperRef, false, {
    threshold: [0],
    ignoreSubsequentEntries: true,
  });

  return (
    <Wrapper
      ref={wrapperRef}
      data-content-type={contentData.contentType}
    >
      <DateWrapper>
        {/* Optical alignment */}
        <Prefix style={{ marginLeft: 1 }}>Last updated on</Prefix>
        <Title>{humanReadableDate}</Title>
      </DateWrapper>
      <HitsWrapper>
        <Prefix># of hits</Prefix>
        <Spacer size={5} />
        {typeof metrics.hits === 'number' && hasReachedFooter && (
          <HitCounter hits={metrics.hits} />
        )}
      </HitsWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 72px;

  /*
    We don’t track hits for snippets, so we want to center the date.
  */
  &[data-content-type='snippet'] {
    justify-content: center;
  }
`;

const Prefix = styled.h3`
  color: var(--color-gray-600);
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  font-family: var(--font-family-mono);
  font-style: italic;
  /* Make it look slightly bolder: */
  -webkit-font-smoothing: subpixel-antialiased;

  @media ${BREAKPOINTS.smAndSmaller} {
    font-size: 0.75rem;
  }
`;

const Title = styled.p`
  font-size: 1.125rem;
  font-weight: var(--font-weight-medium);
  margin-top: 0.325rem;
`;

const DateWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Wrapper}[data-content-type='snippet'] & {
    text-align: center;
  }
  ${Wrapper}[data-content-type='blog-post'] & {
    flex: 2;
  }
`;

const HitsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  /* HACK: The hit counter changes height which moves the entire layout. */
  min-height: 4.5rem;
  text-align: right;

  ${Wrapper}[data-content-type='snippet'] & {
    display: none;
  }
`;

export default BlogPostContentFooter;
